import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from './button';

/**
 * Modal component with configurable content, title, actions, and a 'Don't show again' checkbox.
 *
 * Props:
 * - open: boolean (controls visibility)
 * - onClose: function (called when modal is closed)
 * - title: string | ReactNode (modal title)
 * - children: ReactNode (modal content)
 * - showDontShowAgain: boolean (show/hide the checkbox)
 * - dontShowAgainLabel: string (label for the checkbox)
 * - onDontShowAgainChange: function (called with new value)
 * - dontShowAgainChecked: boolean (controlled checked state)
 * - actions: ReactNode (custom action buttons, e.g. <Button>OK</Button>)
 * - className: string (optional extra classes for modal)
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  showDontShowAgain = false,
  dontShowAgainLabel = "Don't show this again",
  onDontShowAgainChange,
  dontShowAgainChecked = false,
  actions,
  className = '',
}) => {
  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden ${className}`}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
            {/* Title */}
            {title && (
              <div className="px-8 pt-8 pb-2 text-center">
                <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              </div>
            )}
            {/* Content */}
            <div className="px-8 py-4 text-gray-700 text-base">
              {children}
            </div>
            {/* Don't show again checkbox */}
            {showDontShowAgain && (
              <div className="px-8 pb-2 flex items-center">
                <input
                  id="dont-show-again"
                  type="checkbox"
                  checked={dontShowAgainChecked}
                  onChange={e => onDontShowAgainChange && onDontShowAgainChange(e.target.checked)}
                  className="mr-2 accent-black h-4 w-4 rounded"
                />
                <label htmlFor="dont-show-again" className="text-sm text-gray-600 cursor-pointer">
                  {dontShowAgainLabel}
                </label>
              </div>
            )}
            {/* Actions */}
            {actions && (
              <div className="px-8 pb-8 pt-2 flex justify-end gap-2">
                {actions}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal; 